import React from 'react';
import { useFamily } from '../../context/FamilyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { deleteChore } from '@coparent/shared/src/services/choreService';
import { DAYS_OF_WEEK_SHORT, CHORE_FREQUENCIES } from '@coparent/shared/src/utils/constants';
import ChildDots from '../common/ChildDots';
import { HiTrash, HiPencil, HiCheckCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';

const todayDow = () => new Date().getDay();

function isChoreScheduledToday(chore) {
  if (chore.frequency === 'daily') return true;
  if (chore.frequency === 'specificDays' || chore.frequency === 'weekly') {
    return chore.dayOfWeek?.includes(todayDow());
  }
  return false;
}

const ChoreList = ({ chores, completions, onEdit, onToggleComplete }) => {
  const { familyId, childrenMap } = useFamily();
  const { canDelete } = usePermissions();

  const handleDelete = async (choreId) => {
    if (!window.confirm('Delete this chore?')) return;
    try {
      await deleteChore(familyId, choreId);
      toast.success('Chore deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const isCompleted = (choreId, childId) => {
    return completions.some(c => c.choreId === choreId && c.childId === childId);
  };

  const frequencyLabel = (freq) => {
    const found = CHORE_FREQUENCIES.find(f => f.value === freq);
    return found?.label || freq;
  };

  if (!chores.length) {
    return (
      <div className="text-center py-16 text-ink-300">
        <p className="text-base font-semibold">No chores yet</p>
        <p className="text-sm mt-1">Add chores to track tasks across both households.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {chores.map(chore => {
        const scheduled = isChoreScheduledToday(chore);

        return (
          <div key={chore.id} className="card-hover p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${scheduled ? 'bg-green-500' : 'bg-ink-200'}`} />
                <h3 className="font-bold text-ink-900">{chore.name}</h3>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onEdit(chore)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-ink-600 hover:bg-surface-100 transition-colors"
                >
                  <HiPencil className="text-sm" />
                </button>
                {canDelete && (
                  <button
                    onClick={() => handleDelete(chore.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
                  >
                    <HiTrash className="text-sm" />
                  </button>
                )}
              </div>
            </div>

            {chore.description && (
              <p className="text-sm text-ink-400 mt-1.5">{chore.description}</p>
            )}

            {/* Schedule */}
            <div className="flex items-center gap-2 mt-3">
              {chore.frequency === 'daily' ? (
                <span className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-emerald-50 text-emerald-600">
                  Daily
                </span>
              ) : (
                <div className="flex gap-1.5">
                  {DAYS_OF_WEEK_SHORT.map((day, i) => (
                    <span
                      key={i}
                      className={`text-[0.625rem] px-2 py-1 rounded-lg font-bold uppercase tracking-wide ${
                        chore.dayOfWeek?.includes(i)
                          ? 'bg-ink-800 text-white'
                          : 'bg-surface-100 text-ink-300'
                      }`}
                    >
                      {day}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Children */}
            {chore.childIds?.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2">
                <ChildDots childIds={chore.childIds} childrenMap={childrenMap} showNames />
              </div>
            )}

            {/* Today's completion checkboxes */}
            {scheduled && chore.childIds?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-surface-100 flex gap-3 flex-wrap">
                {chore.childIds.map(childId => {
                  const done = isCompleted(chore.id, childId);
                  const child = childrenMap?.[childId];
                  return (
                    <button
                      key={childId}
                      onClick={() => onToggleComplete(chore.id, childId)}
                      className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors ${
                        done
                          ? 'bg-green-50 text-green-700'
                          : 'bg-surface-50 text-ink-400 hover:bg-surface-100'
                      }`}
                    >
                      <HiCheckCircle className={`text-sm ${done ? 'text-green-500' : 'text-ink-200'}`} />
                      <span className={done ? 'line-through' : ''}>{child?.name || ''}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ChoreList;
